import { createRoot } from "react-dom/client";
import React from "react";
import * as Sentry from "@sentry/react";

import ContainerBoundaryContext from "common/boundary/ContainerBoundaryContext";
import { FetchError } from "common/fetch/errors";

import { getSentryUserdata } from "./initialization";

const extractMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  if (error) {
    return `${error}`;
  }

  return null;
};

const demoteToWarningMessageChecks = [
  // We handle chunk load errors in the UI, there is no reason to alert on them.
  (m: string) => /^Loading chunk \d+ failed after \d+ retries./.test(m),
];

type RenderOptions = {
  hasSessionRecording?: boolean;
  hasOnErrorRecording?: boolean;
};

const initSentry = (options: RenderOptions) => {
  if (!window.SENTRY_DSN) {
    console.warn("Sentry is not enabled because SENTRY_DSN is not set");
    return;
  }

  // We'll set the DNS / Release values
  Sentry.init({
    dsn: window.SENTRY_DSN,
    release: RELEASE_VERSION,
    attachStacktrace: true, // Attach stack traces to any message rather than just exceptions
    denyUrls: [
      // Prevent any GTM errors from being sent since we cannot take action on these
      /googletagmanager\.com\//i,
      // Prevent any local file errors from being sent if someone downloads the page
      /^file:\/\//i,
      // Prevent any Pixel errors from being sent since we cannot take action on these
      /cdn\.popt\.in\//i,
      // Webkit beef
      /@webkit-masked-url/i,
    ],
    ignoreErrors: [
      // filter out these GTM errors: https://vetcove.sentry.io/issues/4339138541/, https://vetcove.sentry.io/issues/4343020355/
      "Non-Error promise rejection captured with value: Object Not Found Matching Id",
      // filter out UnhandledRejection errors that have no information: https://vetcove.sentry.io/issues/3316237761
      "Non-Error promise rejection captured with value: undefined",
      // filter out ResizeObserver loop completed with undelivered notifications: https://vetcove.sentry.io/issues/3890839167/
      "ResizeObserver loop completed with undelivered notifications",
    ],
    beforeSend: (event, hint) => {
      // Ignore FetchError exceptions since they are handled by the application
      if (hint.originalException instanceof FetchError) {
        return null;
      }

      const message = extractMessage(hint.originalException);

      if (message && demoteToWarningMessageChecks.some((check) => check(message))) {
        return { ...event, level: "warning" };
      }

      return event;
    },
    // If we have global session recording enabled, we'll want to record errors when they happen so we can
    // get a better understanding of how it was triggered and what the failure state looks like.
    replaysOnErrorSampleRate: options.hasOnErrorRecording ? 1 : 0,
    // If we have session recording enabled on a user, we'll want to record them throughout the session
    replaysSessionSampleRate: options.hasSessionRecording ? 1 : 0,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
      }),
    ],
  });

  // We'll attempt to get the user data out of the document
  const sentryUserData = getSentryUserdata();

  // If it exists, let's set the user
  if (sentryUserData) {
    Sentry.setUser(sentryUserData);
  }
};

const render = (App: React.ComponentType<any>, options: RenderOptions) => {
  initSentry(options);

  const reactBody = document.getElementById("ReactBody");

  if (reactBody) {
    const root = createRoot(reactBody);

    root.render(
      <React.StrictMode>
        <ContainerBoundaryContext.Provider value={reactBody}>
          <App />
        </ContainerBoundaryContext.Provider>
      </React.StrictMode>,
    );
  }
};

export default render;
